import React, { useState } from 'react'
import { Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import { showLayer } from '@/utils/layer'
import { getMemberBirthdayList } from '../api'
import MemberDetail from '../member-detail/member-detail'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

const getDaysInMonth = (year: number, month: number) => dayjs(new Date(year, month - 1, 1)).daysInMonth()

const getNextBirthday = (today: dayjs.Dayjs, month: number, date: number) => {
    let year = today.year()
    let daysInMonth = getDaysInMonth(year, month)
    if (date > daysInMonth && !(month === 2 && date === 29)) return undefined

    while (date > daysInMonth) {
        year += 1
        daysInMonth = getDaysInMonth(year, month)
    }

    let nextBirthday = dayjs(new Date(year, month - 1, date)).startOf('day')
    while (nextBirthday.isBefore(today)) {
        year += 1
        daysInMonth = getDaysInMonth(year, month)
        if (date > daysInMonth) continue
        nextBirthday = dayjs(new Date(year, month - 1, date)).startOf('day')
    }

    return nextBirthday
}

const getBirthdayInfo = (birthday?: string) => {
    if (!birthday) return { dateText: '--', dayText: '--' }

    const matched = birthday.match(/(\d{1,2})-(\d{1,2})$/)
    const month = matched ? Number(matched[1]) : dayjs(birthday).month() + 1
    const date = matched ? Number(matched[2]) : dayjs(birthday).date()
    if (!month || month < 1 || month > 12 || !date || date < 1) return { dateText: birthday, dayText: '--' }

    const today = dayjs().startOf('day')
    const nextBirthday = getNextBirthday(today, month, date)
    if (!nextBirthday) return { dateText: birthday, dayText: '--' }

    return {
        dateText: `${month}月${date}日`,
        dayText: `${nextBirthday.diff(today, 'day')}天`,
    }
}

const MemberBirthday: React.FC = () => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const req = useRequest(() => getMemberBirthdayList({ page, limit: pageSize }), {
        refreshDeps: [page, pageSize],
        onError: e => $msg('error', e.message || '获取会员生日失败'),
    })
    const openMemberDetail = (memberId?: number) => {
        if (!memberId) return

        showLayer(<MemberDetail drawer memberId={memberId} onSuccess={() => req.refresh()} />)
    }
    const columns: ColumnsType<any> = [
        {
            title: '会员',
            dataIndex: 'member',
            width: 180,
            render: value => value?.name || value?.user?.nickname || '--',
        },
        { title: '手机号', dataIndex: ['member', 'phone'], width: 140, render: value => value || '--' },
        { title: '会员卡号', dataIndex: ['member', 'card_code'], width: 140, render: value => value || '--' },
        { title: '生日', dataIndex: 'solar_birthday', width: 140, render: value => getBirthdayInfo(value).dateText },
        {
            title: '距离生日',
            dataIndex: 'solar_birthday',
            width: 140,
            render: value => getBirthdayInfo(value).dayText,
        },
        {
            title: '操作',
            width: 88,
            fixed: 'right',
            render: (_value, record) => (
                <span className="action" onClick={() => openMemberDetail(record.member?.id)}>
                    详情
                </span>
            ),
        },
    ]

    return (
        <SettingPage
            title="会员生日"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                <>
                    <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                        刷新
                    </Button>
                    <span className="member-basic-setting__stat">
                        未录入生日会员数：{req.data?.member_birthday_miss_count || 0}
                    </span>
                </>
            }
        >
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => record.member?.id || index}
                    loading={req.loading}
                    columns={columns}
                    dataSource={req.data?.member_list || []}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default MemberBirthday
